import React, { useContext, useState } from 'react'
import dynamic from 'next/dynamic'
import { useSelector } from 'react-redux'
import { getSearchPaxes, getNights } from 'src/effects/Search/selectors'
import { RoomsContext } from '../context'
import { useS4LContext } from '@s4l/react-context'
import { Typography } from '@s4l/ds'
import { IconArrowLineDown } from '@s4l/icons/IconArrowLineDown'

import Facilities from './Facilities'

import BedType from 'src/comps/BedType'
import type { Board } from '../Collection/RoomsCollection'
import Capacity from 'src/comps/Capacity'
import Loader from 'src/comps/Atom/Loader'

import styles from './styles.module.css'

import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { getLocation } from 'client/contexts/Location/selectors'
import Allotment from './Allotment'
import OfferResponsive from './OfferResponsive'
import type { ImageApiObject } from 'src/types/image'
import { RoomDetailsModal } from './RoomDetailsContainer'
import RoomOptionsToggle from './RoomOptionsToggle'
import RoomImage from './RoomImage'
import ButtonLink from 'src/comps/Atom/Buttons/ButtonLink'
import { useDevice } from 'src/hooks/useDevice'
import { BreakpointSizes } from 'src/types/device'
import useBus from 'src/hooks/useBus'
import { HIGHLIGHTED_OFFER_EXPERIMENT } from 'src/constants/experimentConfig'

const RoomDetails = dynamic(() => import(/* webpackChunkName: "room-details" */ './RoomDetails'), {
  loading: () => <Loader />,
  ssr: false,
})

interface RoomResponsiveProps {
  closeGroup: () => void
  room: Board[]
  affiliateBoard?: string
  isFromMeta?: boolean
  initialOpenOffers?: boolean
  experimentVariant?: number
  affiliateId?: string
}

export const SHOW_MORE_OFFERS_SMALL_LIMIT = 1
export const SHOW_MORE_OFFERS_BIG_LIMIT = 2

const RoomResponsive = ({
  closeGroup,
  room = [],
  affiliateBoard = '',
  isFromMeta = false,
  initialOpenOffers = false,
  experimentVariant,
  affiliateId,
}: RoomResponsiveProps) => {
  const { i18n } = useS4LContext()
  const { sendEvent: sendKafkaClickRoomImages } = useKafkaEvent({ page: 'hotel_details' })

  const { handleSelectRoom, highlightedRoomUniqueId, isMultiRoom, selectedRooms, isBookingRoom } =
    useContext(RoomsContext)

  const nights = useSelector(getNights)
  const occupancies = useSelector(getSearchPaxes)
  const locationState = useSelector(getLocation)

  const [isOpenOffers, setIsOpenOffers] = useState(initialOpenOffers)
  const [isOpenDetails, setIsOpenDetails] = useState(false)

  const { send } = useBus()

  const {
    adults,
    children,
    childrenAges,
    photos,
    roomName,
    roomsNumber,
    beds,
    allotment,
    facilities,
  } = room[0]

  const [mainPhoto] = photos
  const capacity = adults + children

  const { activeBreakpoint } = useDevice()
  const isMobile = activeBreakpoint < BreakpointSizes.M

  const getOffersToShowLimit = isMobile ? SHOW_MORE_OFFERS_BIG_LIMIT : SHOW_MORE_OFFERS_SMALL_LIMIT
  const offersToShow: Board[] = !isOpenOffers ? room.slice(0, getOffersToShowLimit) : room

  const { sendEvent: sendShowMoreOffers } = useKafkaEvent({ page: 'hotel_details' })

  const handleClickDetails = (event: React.UIEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    sendKafkaClickRoomImages(
      { section: SECTIONS.ROOM_IMAGES },
      {
        occupancies,
        search: {
          country: locationState?.country,
          type: locationState?.citySlug ? 'city' : 'zone',
          slug: locationState?.slug,
        },
      }
    )
    setIsOpenDetails(true)
    send('show-room-gallery')
  }

  const handleClickBook = ({ board, isRemove }: { board: Board; isRemove: boolean }) => {
    handleSelectRoom({ board, isRemove, closeGroup })
  }

  const handleClose = () => {
    setIsOpenDetails(false)
    send('hide-room-gallery')
  }

  const handleToggle = () => {
    sendShowMoreOffers({ section: 'room_offers' }, { action: isOpenOffers ? 'close' : 'open' })

    setIsOpenOffers(!isOpenOffers)
  }

  const hasRoomsToShow = !isOpenOffers && room.length > getOffersToShowLimit
  const hasRoomsToHidden = isOpenOffers && room.length > getOffersToShowLimit

  const hasVisibleAffiliateOffer = Boolean(
    affiliateBoard && room.some(board => board.availabilityId === affiliateBoard)
  )

  const isHighlightedRoom =
    hasVisibleAffiliateOffer ||
    Boolean(highlightedRoomUniqueId && room[0]?.roomUniqueId === highlightedRoomUniqueId)

  const showFeaturedRoom =
    isFromMeta &&
    isHighlightedRoom &&
    !isMultiRoom &&
    (experimentVariant === HIGHLIGHTED_OFFER_EXPERIMENT.PROMOTE_TO_FIRST ||
      experimentVariant === HIGHLIGHTED_OFFER_EXPERIMENT.SCROLL_TO_POSITION ||
      experimentVariant === HIGHLIGHTED_OFFER_EXPERIMENT.CURRENT_DESIGN_WITH_HIGHLIGHTED_STYLES)

  const correspondingRoomOptionsToggle = (
    <RoomOptionsToggle
      isOpen={isOpenOffers}
      onClick={handleToggle}
      options={room.length}
      outlinedShowMoreButton={isMobile}
    />
  )

  return (
    <>
      <div className={styles.roomCardWrapper} data-featured={showFeaturedRoom}>
        {showFeaturedRoom && (
          <div className={styles.featuredHeader}>
            <IconArrowLineDown size={24} />
            <Typography variant="body" size="l" as="p" bold>
              {i18n.formatMessage({ id: 'global.highlighted-room' })}
            </Typography>
          </div>
        )}
        <div className={styles.container} data-testid="room">
          <div className={styles.roomContainer}>
            <div className={styles.detailsContainer}>
              {mainPhoto && (
                <RoomImage
                  image={mainPhoto as ImageApiObject}
                  alt={roomName}
                  onClick={handleClickDetails}
                  imagesNumber={photos.length}
                />
              )}
              <div className={styles.roomHeader}>
                <button
                  className={styles.roomNameButton}
                  type="button"
                  data-testid="room-name"
                  onClick={handleClickDetails}
                >
                  <h3 className={styles.roomName}>
                    {roomsNumber > 1 ? `(x${roomsNumber}) ` : ''}
                    {roomName.charAt(0).toUpperCase() + roomName.slice(1)}
                  </h3>
                </button>
                <Allotment allotment={allotment} />
                <div className={styles.detailsCapacityContainer}>
                  <Capacity
                    adults={adults}
                    children={children}
                    childrenAges={childrenAges}
                    showAges={false}
                    title={false}
                    showIcons={false}
                  />
                  {beds.length > 0 && (
                    <>
                      <div className={styles.capacityAndBedTypeSeparator}>·</div>
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      <BedType beds={beds as any} title={false} hideIcons />
                    </>
                  )}
                </div>
                <div className={styles.detailsFacilitiesContainer}>
                  <Facilities facilities={facilities} />
                </div>
                <ButtonLink text="global.see-room" hasChevron onClick={handleClickDetails} />
              </div>
            </div>
            <div data-testid="room-offers-list" className={styles.offersContainer}>
              {offersToShow.map((board, index) => {
                const showAffiliate = affiliateBoard === board.availabilityId
                const previousBoard = offersToShow[index - 1]
                const previousShowAffiliate =
                  previousBoard && affiliateBoard === previousBoard.availabilityId
                const boardSelected = selectedRooms.find(
                  (selectedRoom: Board) => selectedRoom.availabilityId === board.availabilityId
                )
                const isLastColapsedOffer = index === getOffersToShowLimit - 1
                const showSeparator = index > 0 && !showAffiliate && !previousShowAffiliate

                return (
                  <React.Fragment key={board.availabilityId}>
                    {showSeparator && <div className={styles.offerSeparator} />}
                    <OfferResponsive
                      board={board}
                      isLoading={isBookingRoom}
                      isMultiRoom={isMultiRoom}
                      isSelected={!!boardSelected}
                      nights={nights}
                      onClick={handleClickBook}
                      showAffiliate={showAffiliate}
                      affiliateId={affiliateId}
                      prime={board.isPrime}
                      showMore={
                        hasRoomsToShow && isLastColapsedOffer
                          ? correspondingRoomOptionsToggle
                          : undefined
                      }
                      bestPrice={index === 0}
                      isFromMeta={isFromMeta}
                    />
                  </React.Fragment>
                )
              })}
              {hasRoomsToHidden ? (
                <div className={styles.showLessContainer}>{correspondingRoomOptionsToggle}</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <RoomDetailsModal isOpen={isOpenDetails} handleClose={handleClose}>
        <RoomDetails
          className={styles.roomDetails}
          capacity={capacity}
          facilities={facilities}
          photos={photos}
          title={roomName}
          bedTypes={beds}
          modalCallback={handleClose}
        />
      </RoomDetailsModal>
    </>
  )
}

export default RoomResponsive
